import 'braid-design-system/reset';

import { ApolloProvider } from '@apollo/client';
import { device } from '@seek/ca-myprofile-utils';
import { MelwaysProvider } from '@seek/melways-react';
import { getZoneFromSite } from '@seek/melways-sites';
import { setup as setupMetrics, metrics } from '@seek/metrics-js';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import promise from 'es6-promise';
promise.polyfill();
import 'raf/polyfill';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import type { Site } from 'src/config';
import apiClient from 'src/modules/api';
import { initBrowserLog } from 'src/modules/error-logger/browserLog';

import { App } from './components/App/App';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import createStore from './createStore';
import { ApiProvider } from './modules/ApiProvider';
import { createAnalytics } from './modules/analytics/analytics';
import { hydrateFeatures } from './modules/features';
import { updatePageBrandCountryZone } from './reducers/page/actionCreators';
import type { ClientContext } from './types';

export default ({
  language,
  vocabLanguage,
  site,
  brand,
  country,
}: ClientContext) => {
  const zone = getZoneFromSite(site as Site);

  setupMetrics({
    host: window.CONFIG.METRICS_URL,
    isEnabled: window.CONFIG.METRICS_ENABLED,
    prefix: 'ca_settings_ui',
    globalTags: [
      `environment:${window.CONFIG.ENV}`,
      `browser-name:${device.getBrowserName()}`,
      `browser-version:${device.getBrowserVersion()}`,
      `device-type:${device.getDeviceType()}`,
      `app-version:${window.CONFIG.VERSION}`,
      `country:${country}`,
      `language:${language}`,
      `zone:${zone}`,
    ],
  });

  metrics.sendPagePerformance();

  initBrowserLog();

  const api = apiClient({
    useMocks: window.CONFIG.ENV === 'local',
    zone,
  });

  const router = createBrowserRouter([
    {
      path: '/:language?/settings',
      element: <App />,
    },
    {
      path: '/:language?/settings/visibility',
      element: <App />,
    },
    {
      path: '/:language?/settings/notifications',
      element: <App />,
    },
    {
      path: '/:language?/settings/notifications/unsubscribe',
      element: <App />,
    },
  ]);

  const store = createStore(country, brand, language, zone, api);
  store.dispatch(
    updatePageBrandCountryZone(country, brand, language, zone, site),
  );

  const initialTrackingState = {
    brand,
    zone,
    siteCountry: country,
    siteLanguage: language,
    userAgent: global?.navigator?.userAgent,
    actionOrigin: 'settings' as const,
    siteSection: 'myaccount' as const,
  };

  const analytics = createAnalytics(initialTrackingState);

  hydrateFeatures();

  const container = document.getElementById('app');

  hydrateRoot(
    container!,
    <Provider store={store}>
      <VocabProvider language={vocabLanguage}>
        <MelwaysProvider.Client site={site}>
          <ApolloProvider client={api.graphqlClient}>
            <ApiProvider zone={zone}>
              <AnalyticsProvider value={analytics}>
                <BraidProvider theme={seekJobs}>
                  <ToastProvider>
                    <RouterProvider router={router} />
                  </ToastProvider>
                </BraidProvider>
              </AnalyticsProvider>
            </ApiProvider>
          </ApolloProvider>
        </MelwaysProvider.Client>
      </VocabProvider>
    </Provider>,
  );
};
